import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import type { PostMeta } from '@/types/blog';
import { formatDate } from '@/app/content/blog';
import { blogCardContent } from '@/app/content/uiContent';

const CATEGORY_STYLES: Record<string, string> = {
  Prawo: 'bg-teal-50 text-[#004f4f] ring-1 ring-teal-200',
  Dane: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  Metodologia: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
};

interface BlogCardProps {
  post: PostMeta;
  featured?: boolean;
}

export function BlogCard({ post, featured = false }: BlogCardProps) {
  const categoryStyle = CATEGORY_STYLES[post.category] ?? 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';

  if (featured) {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group block col-span-full bg-white border border-gray-200 rounded-2xl p-8 md:p-10 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] hover:border-gray-300 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4"
        aria-label={`Przejdź do artykułu: ${post.title}`}
      >
        <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${categoryStyle}`}>
                {post.category}
              </span>
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{blogCardContent.featuredBadgeLabel}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight tracking-tight mb-4 group-hover:text-[#004f4f] transition-colors">
              {post.title}
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-6 line-clamp-3">
              {post.description}
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <span>{formatDate(post.date)}</span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} aria-hidden="true" />
                {post.readingTime} min czytania
              </span>
            </div>
          </div>
          <div className="flex-shrink-0 self-center md:self-start">
            <span className="inline-flex items-center gap-2 text-[#004f4f] font-semibold text-sm group-hover:gap-3 transition-all">
              {blogCardContent.readLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-gray-300 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4"
      aria-label={`Przejdź do artykułu: ${post.title}`}
    >
      <span className={`self-start text-xs font-semibold px-3 py-1 rounded-full mb-4 ${categoryStyle}`}>
        {post.category}
      </span>
      <h2 className="text-xl font-bold text-slate-900 leading-snug tracking-tight mb-3 line-clamp-2 group-hover:text-[#004f4f] transition-colors">
        {post.title}
      </h2>
      <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3 flex-1">
        {post.description}
      </p>
      <div className="flex items-center justify-between text-xs text-slate-600 pt-4 border-t border-gray-100">
        <span>{formatDate(post.date)}</span>
        <span className="flex items-center gap-1.5">
          <Clock size={13} aria-hidden="true" />
          {post.readingTime} {blogCardContent.readMinutesShortSuffix}
        </span>
      </div>
    </Link>
  );
}
